import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext } from 'react'
import { ShopMarketplaceButtons } from '~/components/ShopMarketplaceButtons'
import { StakeListContext } from '../context'
import { useInventory } from '../hooks/useInventory'
import { InventoryCard } from './InventoryCard'
import { InventoryRow } from './InventoryRow'

/** `(operative_type, operative_id)` is the real identity — ids repeat across types. */
const itemKey = (nft: NFT) => `${nft.operative_type}#${nft.operative_id}`

/**
 * Auto-fill grid. The page this replaces measured its own column count once on mount, off a
 * hidden off-screen card used as a ruler, and so never reflowed on resize. CSS does it properly.
 */
export const InventoryGrid = () => {
    const { data, view } = useContext(StakeListContext)
    const { data: all, isFetched } = useInventory()

    if (!isFetched && all.length === 0) return <Loading />
    if (all.length === 0) return <EmptyInventory />
    if (data.length === 0) return <NoMatches />

    if (view === 'list') {
        return (
            // One column of rows below sm, two from md — a 1400px page fits two comfortably and a
            // single column of 72px rows across a wide monitor is a lot of empty gutter.
            <div className='grid grid-cols-1 md:grid-cols-2 auto-rows-max gap-2'>
                {data.map((nft) => (
                    <InventoryRow key={itemKey(nft)} nft={nft} />
                ))}
            </div>
        )
    }

    return (
        // Two columns are pinned below sm: at 375px the auto-fill minimum collapsed to a single
        // full-width card, which is a poor way to browse hundreds of items.
        <div className='grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] auto-rows-max gap-3 sm:gap-4'>
            {data.map((nft) => (
                <InventoryCard key={itemKey(nft)} nft={nft} />
            ))}
        </div>
    )
}

const Loading = () => (
    <div className='flex flex-row items-center justify-center gap-3 py-24 text-white/50 font-bold uppercase'>
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading your inventory</span>
    </div>
)

const EmptyInventory = () => (
    <div className='flex flex-col items-center justify-center gap-6 py-16 text-center'>
        <div className='flex flex-col gap-2'>
            <h2 className='text-xl font-black uppercase'>Your inventory is empty</h2>
            <p className='text-white/50 font-bold'>Pick up an Operative, Weapon Skin or Loot Box to start earning.</p>
        </div>
        <ShopMarketplaceButtons />
    </div>
)

const NoMatches = () => (
    <div className='flex flex-col items-center justify-center gap-2 py-16 text-center'>
        <h2 className='text-xl font-black uppercase'>No items match</h2>
        <p className='text-white/50 font-bold'>Try a different category, status or ID.</p>
    </div>
)
